import { Typography, TypographyProps } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";

type StoreTimeCountingStringProps = {
  text: string;
  endTime: string;
} & TypographyProps;

const PLACEHOLDER_REGEX = /\{\{\s*expirationTime\s*\}\}/g;

const StoreTimeCountingString = ({
  ...props
}: StoreTimeCountingStringProps) => {
  const { text, endTime, variant = "body1" } = props;

  const endTimeMs = useMemo(() => new Date(endTime).getTime(), [endTime]);
  const [timeLeftMs, setTimeLeftMs] = useState<number>(() => {
    const initial = endTimeMs - Date.now();
    return isNaN(initial) ? 0 : Math.max(0, initial);
  });

  useEffect(() => {
    if (!endTimeMs || isNaN(endTimeMs)) {
      setTimeLeftMs(0);
      return;
    }

    const update = () => {
      const diff = endTimeMs - Date.now();
      setTimeLeftMs(Math.max(0, diff));
    };

    update();
    const intervalId = setInterval(update, 1000);
    return () => clearInterval(intervalId);
  }, [endTimeMs]);

  const formattedDuration = useMemo(() => {
    const totalSeconds = Math.max(0, Math.floor(timeLeftMs / 1000));
    const totalMinutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const mm = String(totalMinutes).padStart(2, "0");
    const ss = String(seconds).padStart(2, "0");
    return `${mm}:${ss}`;
  }, [timeLeftMs]);

  const segments = useMemo(() => {
    if (!text) return [""];
    return text.split(PLACEHOLDER_REGEX);
  }, [text]);

  const { text: _t, endTime: _e, ...typographyProps } = props as any;

  return (
    <Typography variant={variant} {...typographyProps}>
      {segments.map((seg, idx) => (
        <React.Fragment key={`seg-${idx}`}>
          {seg}
          {idx < segments.length - 1 ? (
            <strong key={`time-${idx}`}>{formattedDuration}</strong>
          ) : null}
        </React.Fragment>
      ))}
    </Typography>
  );
};

export default StoreTimeCountingString;
